import React from 'react';
import { SL, STATUS_COLORS, INV_COLORS, RL } from '../constants.js';

// ── Icon (Lucide) ────────────────────────────
export const Icon = ({name, size=16, color='currentColor', strokeWidth=2, style={}}) => {
  const raw = name.replace(/-(\w)/g, (_, c) => c.toUpperCase());
  const key = raw.charAt(0).toUpperCase() + raw.slice(1);
  const d = window.lucide && window.lucide[key];
  if (!d) return <span style={{display:'inline-block',width:size,height:size}}/>;
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size}
      viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
      style={{flexShrink:0,...style}}>
      {(d[2]||[]).map((child,i)=>{
        if(!child||!child[0]) return null;
        const [tag,attrs]=child;
        return React.createElement(tag,{key:i,...(attrs||{})});
      })}
    </svg>
  );
};

export const Spin = () => <Icon name="loader" size={14} style={{animation:'spin 1s linear infinite'}}/>;

// ── Pill / Status chips ────────────────────────

const Pill = ({val, map, colorMap}) => {
  if(!val) return null;
  const label = (map||{})[val] || val;
  const base = {display:'inline-block',padding:'2px 9px',borderRadius:20,fontSize:11,fontWeight:600,border:'1px solid'};
  const c = (colorMap||{})[val] || {bg:'#F3F4F6',color:'#6B7280',border:'#E5E7EB'};
  return <span style={{...base,background:c.bg,color:c.color,borderColor:c.border}}>{label}</span>;
};

export const StatusPill = ({val}) => <Pill val={val} map={SL} colorMap={STATUS_COLORS}/>;
export const InvPill = ({val}) => <Pill val={val} colorMap={INV_COLORS}/>;

// StatusPicker: pill cliccabile che apre un menu con gli stati selezionabili
export const StatusPicker = ({value, options, labels, colors, onChange, disabled}) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if(!open) return;
    const onDoc = e => { if(ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = e => { if(e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const labelMap = labels || {};
  const colorMap = colors || {};
  const pick = v => { setOpen(false); if(v !== value) onChange(v); };

  const currLabel = labelMap[value] || value;
  const currColor = colorMap[value] || {bg:'#F3F4F6',color:'#6B7280',border:'#E5E7EB'};

  return (
    <div ref={ref} style={{position:'relative',display:'inline-block'}}>
      <button
        onClick={() => !disabled && setOpen(o=>!o)}
        disabled={disabled}
        style={{
          display:'inline-flex',alignItems:'center',gap:6,
          padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:600,
          border:'1px solid '+currColor.border,background:currColor.bg,color:currColor.color,
          cursor:disabled?'default':'pointer',
          transition:'transform .1s',
        }}
        onMouseEnter={e => !disabled && (e.currentTarget.style.transform='scale(1.03)')}
        onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}
        title={disabled ? currLabel : 'Clicca per cambiare stato'}>
        {currLabel}
        {!disabled && <Icon name="chevron-down" size={10} color={currColor.color} strokeWidth={2.5}/>}
      </button>
      {open && (
        <div style={{
          position:'absolute',top:'calc(100% + 4px)',left:0,zIndex:50,
          background:'var(--card)',border:'1px solid var(--border)',borderRadius:10,
          boxShadow:'0 8px 24px rgba(14,16,40,.12)',padding:5,minWidth:160,
        }}>
          {options.filter(o => o !== value).map(o => {
            const c = colorMap[o] || {bg:'#F3F4F6',color:'#6B7280',border:'#E5E7EB'};
            return (
              <div key={o} onClick={() => pick(o)}
                style={{
                  display:'flex',alignItems:'center',gap:8,padding:'7px 9px',
                  borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:500,
                  color:'var(--n700)',
                }}
                onMouseEnter={e => e.currentTarget.style.background='var(--n50)'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <span style={{
                  width:10,height:10,borderRadius:'50%',flexShrink:0,
                  background:c.bg,border:'2px solid '+c.color,
                }}/>
                <span>{labelMap[o] || o}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const MarginChip = ({pct}) => {
  if(pct==null) return <span style={{color:'var(--n400)',fontSize:12}}>-</span>;
  const c = pct>=30?'#065F46':pct>=10?'#92400E':'#991B1B';
  const bg = pct>=30?'#D1FAE5':pct>=10?'#FEF3C7':'#FEE2E2';
  return <span style={{background:bg,color:c,padding:'2px 8px',borderRadius:20,fontSize:11,fontWeight:700}}>{pct}%</span>;
};

export const StatusDot = ({status, size=10}) => {
  const cfg = {
    draft:    {bg:'#E5E7EB',title:'Non inviato'},
    sent:     {bg:'#F59E0B',title:'Inviato - in attesa'},
    accepted: {bg:'#10B981',title:'Confermato'},
    declined: {bg:'#EF4444',title:'Declinato'},
  }[status||'draft'] || {bg:'#E5E7EB',title:'Non inviato'};
  return (
    <span title={cfg.title} style={{display:'inline-block',
      width:size,height:size,borderRadius:'50%',background:cfg.bg,
      flexShrink:0,boxShadow:'0 0 0 2px rgba(0,0,0,.06)'}}/>
  );
};

// ── Layout primitives ────────────────────────
export const Drawer = ({open, onClose, title, subtitle, width=520, children, actions}) => {
  if(!open) return null;
  return(
    <>
      <div style={{position:'fixed',inset:0,background:'rgba(14,16,40,.25)',
        backdropFilter:"blur(2px)",zIndex:300}} /* overlay: no click-to-close *//>
      <div style={{position:'fixed',top:0,right:0,bottom:0,width,
        background:'var(--card)',borderLeft:'1px solid var(--border)',
        zIndex:301,display:'flex',flexDirection:'column',
        boxShadow:'-4px 0 24px rgba(0,0,0,.1)',overflowY:'hidden'}}>
        <div style={{padding:'18px 24px',borderBottom:'1px solid var(--border)',
          display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
          <div>
            <div style={{fontWeight:700,fontSize:16,color:'var(--n800)'}}>{title}</div>
            {subtitle&&<div style={{fontSize:12,color:'var(--n400)',marginTop:2}}>{subtitle}</div>}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            {actions}
            <button className="btn btn-ghost btn-sm" onClick={onClose} style={{padding:'6px'}}>
              <Icon name="x" size={16} color="var(--n500)"/>
            </button>
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'20px 24px'}}>
          {children}
        </div>
      </div>
    </>
  );
};

export const Modal = ({title, onClose, children}) => (
  <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div className="modal">
      <div className="flex-between" style={{marginBottom:20}}>
        <div className="modal-title">{title}</div>
        <button className="btn btn-ghost btn-sm" onClick={onClose} style={{padding:'5px'}}>
          <Icon name="x" size={15} color="var(--n500)"/>
        </button>
      </div>
      {children}
    </div>
  </div>
);

// Modal largo centrale — per dettaglio cantiere e schermate complesse
export const LargeModal = ({open, onClose, title, subtitle, children, actions, widthPct=80}) => {
  if(!open) return null;
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(14,16,40,.45)',
      backdropFilter:'blur(4px)',zIndex:500,display:'flex',
      alignItems:'center',justifyContent:'center'}}>
      <div style={{background:'var(--card)',border:'1px solid var(--border)',
        borderRadius:14,boxShadow:'var(--sh-lg)',
        width:`${widthPct}vw`,maxWidth:1500,minWidth:720,
        height:'85vh',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{padding:'18px 28px',borderBottom:'1px solid var(--border)',
          display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0,
          background:'var(--n0)'}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:700,fontSize:18,color:'var(--n800)',
              overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{title}</div>
            {subtitle&&<div style={{fontSize:13,color:'var(--n400)',marginTop:3}}>{subtitle}</div>}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0,marginLeft:16}}>
            {actions}
            <button className="btn btn-ghost btn-sm" onClick={onClose} style={{padding:'6px'}}>
              <Icon name="x" size={16} color="var(--n500)"/>
            </button>
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto',overflowX:'hidden',padding:'24px 28px',minHeight:0}}>
          {children}
        </div>
      </div>
    </div>
  );
};


export const Tabs = ({tabs, active, onChange}) => (
  <div style={{display:'flex',gap:0,borderBottom:'2px solid var(--n100)',marginBottom:20}}>
    {tabs.map(([id,label])=>(
      <button key={id} onClick={()=>onChange(id)} style={{
        padding:'8px 16px',fontSize:13,fontWeight:active===id?600:400,
        color:active===id?'var(--blue)':'var(--n400)',
        background:'none',border:'none',cursor:'pointer',
        borderBottom:active===id?'2px solid var(--blue)':'2px solid transparent',
        marginBottom:-2,transition:'all .15s',
      }}>{label}</button>
    ))}
  </div>
);

export const FG = ({label, children, span}) => (
  <div className="form-group" style={span?{gridColumn:'span '+span}:{}}>
    <label>{label}</label>
    {children}
  </div>
);

export const RG = ({roles, onChange}) => (
  <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
    {Object.entries(RL).map(([k,v])=>{
      const on = (roles||[]).includes(k);
      return <span key={k} onClick={()=>onChange(k)} style={{cursor:'pointer',
        padding:'4px 9px',borderRadius:6,fontSize:11,fontWeight:on?600:400,
        background:on?'var(--blue-50)':'var(--n50)',
        border:on?'1.5px solid var(--blue)':'1.5px solid var(--border)',
        color:on?'var(--blue-dark)':'var(--n400)',transition:'all .1s'}}>{v}</span>;
    })}
  </div>
);


// ── Pagination ────────────────────────────────
import { PAGE_SIZE } from '../constants.js';

export const Pagination = ({total, limit, offset, onChange}) => {
  if (!total || total <= (limit||PAGE_SIZE)) return null;
  const pageSize = limit || PAGE_SIZE;
  const currentPage = Math.floor(offset / pageSize) + 1;
  const totalPages = Math.ceil(total / pageSize);
  const from = offset + 1;
  const to = Math.min(offset + pageSize, total);

  const goTo = (page) => {
    const newOffset = (page - 1) * pageSize;
    onChange(newOffset);
  };

  // Generate page numbers with ellipsis
  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
      pages.push(p);
    } else if (pages[pages.length-1] !== '...') {
      pages.push('...');
    }
  }

  return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
      padding:'12px 0',borderTop:'1px solid var(--border-soft)',marginTop:8}}>
      <span style={{fontSize:12,color:'var(--n400)'}}>
        {from}–{to} di {total}
      </span>
      <div style={{display:'flex',alignItems:'center',gap:2}}>
        <button className="btn btn-ghost btn-sm" disabled={currentPage<=1}
          onClick={()=>goTo(currentPage-1)} style={{padding:'4px 8px'}}>
          <Icon name="chevron-left" size={14} color="var(--n500)"/>
        </button>
        {pages.map((p,i) => p === '...' ? (
          <span key={`e${i}`} style={{padding:'0 4px',fontSize:12,color:'var(--n400)'}}>...</span>
        ) : (
          <button key={p} onClick={()=>goTo(p)}
            style={{minWidth:28,height:28,borderRadius:6,fontSize:12,fontWeight:p===currentPage?700:400,
              background:p===currentPage?'var(--blue)':'transparent',
              color:p===currentPage?'#fff':'var(--n500)',
              border:'none',cursor:'pointer',transition:'all .1s'}}>
            {p}
          </button>
        ))}
        <button className="btn btn-ghost btn-sm" disabled={currentPage>=totalPages}
          onClick={()=>goTo(currentPage+1)} style={{padding:'4px 8px'}}>
          <Icon name="chevron-right" size={14} color="var(--n500)"/>
        </button>
      </div>
    </div>
  );
};
